import { KalDate } from '@kalidea/kaligraphi';
import padStart from 'lodash-es/padStart';
import partition from 'lodash-es/partition';
import { arrayNotEmpty } from 'src/app/utils/array.utils';
import { toDoubleDigit } from 'src/app/utils/number.utils';
import { ParamModeleEchancierModel } from 'src/graphql/generated-types';

export enum FirstDueDateTypeTypes {
    EURO = '€',
    PERCENTAGE = '%'
}

export class BaseActivityRepaymentSchedule {
    amount?: number;

    numberOfDueDate?: number; // NombreEcheance

    // nombre d'échéance initiale, utilisée uniquement pour la modif de l'échéancier
    // pour affichage seulement
    numberOfDueDateCreation?: number;

    firstChequeNumber?: string;

    useConsecutiveChequeNumber?: boolean;

    paymentDayOfMonth?: number; // JourDansMois

    paymentFrequency?: number; // Periodicite

    dueDateAmount?: number;

    bankName?: string;

    transactionName?: string;

    firstDueMonth?: number; // MoisPremiereEcheance

    firstDueDelay?: number; // DelaiPremiereEcheance

    firstDueDateValue?: number;

    firstDueDateType?: FirstDueDateTypeTypes;

    paymentStartDate: KalDate = new KalDate();

    ibanId?: number;

    modelId: number; // IdModeleEcheancier

    // le libellé de l'échéancier
    label ? = '';
}


export interface RepaymentScheduleRow {
    Id?: number;

    LibelleEcheance: string;

    DateEcheance: KalDate;

    NumeroCheque: string;

    NomBanque: string;

    NomTire: string;

    MontantEcheance: number;

    IsRemisEnBanque?: boolean;

    DateRemisEnBnq?: any;
}


export class SharedRepaymentSchedule extends BaseActivityRepaymentSchedule {

    private _startDate: KalDate;
    private _schedule?: RepaymentScheduleRow[];
    private alreadyPayedRows?: RepaymentScheduleRow[];

    constructor(parameters?: Partial<SharedRepaymentSchedule>) {
        super();
        if (parameters) {
            Object.keys(parameters)
                .filter(key => typeof parameters[key] !== 'undefined')
                .forEach(key => this[key] = parameters[key]);
        }
    }

    get alreadyPayedRow(): RepaymentScheduleRow[] {
        return this.alreadyPayedRows;
    }

    get schedule(): RepaymentScheduleRow[] {
        return this._schedule;
    }

    set schedule(schedule: RepaymentScheduleRow[]) {
        // on historise les lignes déjà payées
        const [remisEnBanque, nonRemisEnBanque] = partition(schedule, ['IsRemisEnBanque', true]);
        this._schedule = nonRemisEnBanque;
        if (!this.alreadyPayedRow) {
            this.alreadyPayedRows = remisEnBanque;
        }
    }

    reset(): void {
        this.update('paymentFrequency', 1);
        this.update('numberOfDueDate', 12);
        this.update('paymentStartDate', new KalDate());
        this.update('firstChequeNumber', undefined);
        this.update('firstDueDateValue', 0);
        this.update('firstDueDateType', FirstDueDateTypeTypes.EURO);
        this.update('firstDueDelay', 0);
        this.update('transactionName', '');
        this.update('bankName', '');
        this.update('paymentDayOfMonth', new Date().getDate());
    }

    updateFromModel(modeleEchancier: ParamModeleEchancierModel, paymentStartDate?: KalDate): void {
        // reset
        this.reset();

        const {

            // datePremiereEcheance = datePremiereEcheance.Value.AddDays(
            // If(modeleEcheancier.IsDeclenchementAvant, -modeleEcheancier.NombreJoursDeclenchement, modeleEcheancier.NombreJoursDeclenchement))
            IsDeclenchementAvant,
            NombreJoursDeclenchement,

            // montant
            MontantPremiereEcheance,
            PourcentagePremiereEcheance,

            NombreEcheance,
            Periodicite,

            JourDansMois,
            MoisPremiereEcheance,
            DelaiPremiereEcheance, // nombre de mois avant la première échéance

            IdModeleEcheancier,

        } = modeleEchancier;

        this.modelId = IdModeleEcheancier;

        if (paymentStartDate) {
            this.update('paymentStartDate', paymentStartDate);
        }

        // première mensualité
        if (PourcentagePremiereEcheance) {
            this.update('firstDueDateValue', toDoubleDigit(PourcentagePremiereEcheance));
            this.update('firstDueDateType', FirstDueDateTypeTypes.PERCENTAGE);
        } else if (MontantPremiereEcheance) {
            this.update('firstDueDateValue', toDoubleDigit(MontantPremiereEcheance));
            this.update('firstDueDateType', FirstDueDateTypeTypes.EURO);
        }

        // date
        if (!!JourDansMois && JourDansMois > 0) {
            this.update('paymentDayOfMonth', JourDansMois);
        }
        if(!!MoisPremiereEcheance && MoisPremiereEcheance > 0){
            this.update('firstDueMonth', MoisPremiereEcheance);
        }

        // première échéance

        if (NombreJoursDeclenchement) {
            if (!paymentStartDate) {
                this.update('paymentDayOfMonth', 0);
            }
            this.update('paymentStartDate', this.paymentStartDate.add((IsDeclenchementAvant ? -1 : 1) * NombreJoursDeclenchement, 'days'));
        }

        if(DelaiPremiereEcheance) {
            this.update('firstDueDelay',DelaiPremiereEcheance);
        }

        // temps
        if (NombreEcheance) {
            this.update('numberOfDueDate', NombreEcheance);
        }
        if (Periodicite) {
            this.update('paymentFrequency', Periodicite);
        }
    }

    update(key: keyof BaseActivityRepaymentSchedule, value): void {
        // @ts-ignore
        this[key] = value;
        switch (key) {
            case 'dueDateAmount':
                this.numberOfDueDate = null;
                break;

            case 'numberOfDueDate':
                this.dueDateAmount = !value ? null : this.getDueDateAmount();
                break;

            case 'firstDueDateValue':
            case 'firstDueDateType':
                value = this.getFirstDueDateAmount();
                if (!value) {
                    return;
                }
                if (this.amount === +value) {
                    // Lorsque le montant de la première échéance est égal au montant total
                    // On défini automatiquement le nombre d'échéance à 1
                    // Ainsi que le montant de l'échéance comme étant le montant total
                    this.numberOfDueDate = 1;
                    this.dueDateAmount = this.amount;
                } else if (this.amount === this.dueDateAmount) {
                    this.numberOfDueDate = 2;
                    this.dueDateAmount = this.amount - (+value);
                }
                break;
        }
    }

    getFirstDueDateAmount(): number {
        const firstDueDateValue = this.firstDueDateValue || 0;

        if (this.firstDueDateType === FirstDueDateTypeTypes.PERCENTAGE) {
            return this.amount * (firstDueDateValue / 100);
        }

        return firstDueDateValue;
    }

    getDueDateAmount(): number {
        if (!this.numberOfDueDate) {
            throw new Error('missing numberOfDueDate');
        }

        const firstAmount = this.getFirstDueDateAmount();

        let totalDueAmount = this.amount - firstAmount;
        let realNumberOfDueDate = (this.numberOfDueDate - (!!firstAmount ? 1 : 0));

        if (firstAmount === this.amount) {
            // Lorsque le montant de la première échéance est égal au montant total
            // Il ne peut y avoir que 1 seule échéance correspondant au montant total
            totalDueAmount = this.amount;
            realNumberOfDueDate = 1;
        }

        // on calcul la somme des arrondis pour voir si on dépasse pas le total demandé de plus d'une mensualité
        // sinon on risque de se retrouver avec une dernière échéance en négatif
        let dueDateAmountControl = toDoubleDigit(totalDueAmount / realNumberOfDueDate);
        const totalCalculated = ((dueDateAmountControl * realNumberOfDueDate) + firstAmount);
        if ((totalCalculated - this.amount) > dueDateAmountControl) {
            // si on dépasse, on doit diminuer le calcul de la mensualité ( sans la première échéance fixe ? )
            // todo a finir ça proprement
            const variation = (Math.max(toDoubleDigit((totalCalculated - this.amount) / realNumberOfDueDate), 0.01));
            dueDateAmountControl -= variation;
        }
        return dueDateAmountControl;
    }

    getNumberOfDueDate(): number {
        if (!this.dueDateAmount) {
            throw new Error('missing numberOfDueDate');
        }

        const firstAmount = this.getFirstDueDateAmount();

        let numberOfDueDate = Math.ceil((this.amount - (firstAmount || 0)) / this.dueDateAmount);
        if (firstAmount > 0 && firstAmount < this.amount) {
            // La première échéance n'est pas considérée en tant que tel (Perceval©) lors du calcul
            // On incrémente le nombre d'échéance pour la prendre en compte
            numberOfDueDate++;
        }

        return numberOfDueDate;
    }

    buildSchedule(params?: { prop: keyof BaseActivityRepaymentSchedule, reset?: boolean }): RepaymentScheduleRow[] {
        // par défaut, il ne va manquer que le nombre d'échéance ou montant des échéances
        if (this.numberOfDueDate) {
            this.dueDateAmount = this.getDueDateAmount();
        } else if (this.dueDateAmount) {
            this.numberOfDueDate = this.getNumberOfDueDate();
        }

        this._schedule = (arrayNotEmpty(this._schedule) && !params?.reset)
            ? this._schedule
            : [];

        let sum = 0;

        // reset start date cache
        this.setStartDate();
        const tempSchedule: RepaymentScheduleRow[] = [];

        const scheduleNotPayedRows = this._schedule.filter(r => !!r.IsRemisEnBanque === false);

        // on factorise l'opérateur `?`
        const prop = params?.prop;

        for (let i = 0; i < this.numberOfDueDate; i++) {
            let amount = this.dueDateAmount;
            const date = this.getDueDate(i);
            let label = `Échéance ${(i + 1)}/${this.numberOfDueDate} - ${this.label}`;

            // première échéance
            if (i === 0 && !!this.firstDueDateValue) {
                amount = this.getFirstDueDateAmount();
            }
            // dernière échéance
            if (i === this.numberOfDueDate - 1) {
                amount = this.amount - sum;
            }

            // round before some
            amount = toDoubleDigit(amount);

            sum += amount;

            const oldRepaymentScheduleRow: RepaymentScheduleRow = scheduleNotPayedRows[i];

            // Liste des props du paramétrage des échéances impactant le calcul d'une échéance
            const dateRelatedProps = ['paymentFrequency', 'paymentDayOfMonth', 'paymentStartDate', 'numberOfDueDate', 'amount'];
            const checkRelatedProps = ['firstChequeNumber', 'useConsecutiveChequeNumber'];
            const amountRelatedProps = ['firstDueDateValue', 'firstDueDateType', 'dueDateAmount', 'numberOfDueDate'];
            const scheduleLabel = oldRepaymentScheduleRow?.LibelleEcheance;

            // on utilise le label précédent uniquement si on est à l'initialisation de la page
            if (scheduleLabel && !['numberOfDueDate', 'firstDueDateValue', 'firstDueDateType'].includes(prop)) {
                // vérifie si le début du label existant correspond au label prédéfinit ("Echéance x / x"),
                // si c'est le cas, on remplace le 1 / x par le nouveau nb d'échéances et on garde la fin du label modifié
                if (scheduleLabel.slice(0, label.length - 1) === label.slice(0, -1)) {
                    label = label.concat(scheduleLabel.slice(label.length));
                } else {
                    label = scheduleLabel;
                }
            }

            tempSchedule.push({
                Id: oldRepaymentScheduleRow?.Id || 0,
                LibelleEcheance: label,
                DateRemisEnBnq: oldRepaymentScheduleRow?.DateRemisEnBnq || null,
                IsRemisEnBanque: !!oldRepaymentScheduleRow?.IsRemisEnBanque,
                // Si la prop de paramétrage est rattaché à cette propriété d'échéance
                // On va utiliser la donnée existante et si elle n'existe pas, on va utiliser les données calculées
                DateEcheance: !dateRelatedProps.includes(prop)
                    ? oldRepaymentScheduleRow?.DateEcheance || date
                    : date,
                NumeroCheque: !checkRelatedProps.includes(prop)
                    ? oldRepaymentScheduleRow?.NumeroCheque || this.getChequeNumber(i)
                    : this.getChequeNumber(i),
                NomBanque: prop !== 'bankName'
                    ? oldRepaymentScheduleRow?.NomBanque || this.bankName || null
                    : this.bankName || null,
                NomTire: prop !== 'transactionName'
                    ? oldRepaymentScheduleRow?.NomTire || this.transactionName || null
                    : this.transactionName || null,
                MontantEcheance: !amountRelatedProps.includes(prop)
                    ? oldRepaymentScheduleRow?.MontantEcheance || amount
                    : amount,
            });
        }

        this.updateSchedule(tempSchedule);
        return this._schedule;
    }

    updateSchedule(schedule: RepaymentScheduleRow[]): void {

        // on ajoute les lignes déjà payées
        schedule.unshift(...(this.alreadyPayedRow || []));

        // on tri le tableau par rapport à la date d'échéance
        schedule.sort((a, b) => a.DateEcheance.isBefore(b.DateEcheance) ? -1 : 1);

        this._schedule = schedule;
    }

    getChequeNumber(rowIndex: number): string | null {
        if (this.firstChequeNumber) {
            if (this.useConsecutiveChequeNumber) {
                return padStart('' + (+this.firstChequeNumber + rowIndex), this.firstChequeNumber.length, '0');
            } else if (rowIndex === 0) {
                return this.firstChequeNumber;
            }
        }
        return null;
    }

    // le calcul de date à l'air d'être assez coûteux, il faudrait voir à le cacher si possible
    getDueDate(index: number): KalDate {
        const date = new KalDate(this._startDate);
        return date.add(this.paymentFrequency * index, 'months');
    }

    /**
     * calcul start date of this schedule only once by schedule
     */
    private setStartDate(): void {
        const day = this.paymentDayOfMonth;

        const defaultMonth = this.paymentDayOfMonth < this.paymentStartDate.getDay() ? this.paymentStartDate.getMonth() + 1 : this.paymentStartDate.getMonth();
        let month = !!this.firstDueMonth ? this.firstDueMonth : defaultMonth;
        month += this.firstDueDelay || 0;

        let year = this.paymentStartDate.getYear();
        if ((new KalDate().set('years', year).set('months', month).set('days', day)).isBefore(this.paymentStartDate)) {
            year++;
        }

        this._startDate = new KalDate().set('years', year).set('months', month).set('days', day);
    }

}
